// src/pages/VoorvertoningPage.js
// Publieke voorvertoning van één doorloopje, waarom- of hoe-stuk (soort, zie utils/publiekOverzicht),
// als een rustige versie van de pagina in de app: de beschrijving en de eerste stap helemaal, van de
// andere stappen titel en afbeelding met in plaats van hun tekst vervagende grijze regels (de tekst
// zelf krijgt de site nooit). Een stuk zonder stappen toont zijn beschrijving. De downloads van een
// doorloopje (zoals een praktijkkaart) staan er wel helemaal. De rest staat achter
// de login; wie al is ingelogd gaat meteen naar de pagina in de app. Onderaan zijn buren uit het
// overzicht (utils/publiekOverzicht, buren), dezelfde als in de geprerenderde HTML: anderen uit zijn
// groep als kaart en de vorige en de volgende. 404 als de voorvertoningen uit staan (zie
// utils/publiekeDoorloopjes).
import React, { useState, useEffect } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { BlocksRenderer } from '@strapi/blocks-react-renderer';

import Beschrijving from '../components/Beschrijving';
import PubliekeKaart from '../components/PubliekeKaart';
import PubliekePagina from '../components/PubliekePagina';
import StapKaart from '../components/StapKaart';
import SEO from '../components/SEO';
import { toDescription } from '../utils/seoText';
import { OVERZICHT_PAD, usePubliekeDoorloopjes } from '../utils/publiekeDoorloopjes';
import { SOORTEN, buren, meerTitel, voorvertoningPad } from '../utils/publiekOverzicht';
import { ReactComponent as LockIcon } from '../assets/icon-lock.svg';
import './PubliekeDoorloopjes.css';

const STRAPI_URL = process.env.REACT_APP_STRAPI_URL;

function Buren({ groep, vorige, volgende, verwant }) {
  return (
    <>
      {verwant.length > 0 && (
        <section className="publiek-buren" aria-labelledby="publiek-buren-titel">
          <h2 id="publiek-buren-titel">{meerTitel(groep)}</h2>
          <ul className="publiek-kaarten">
            {verwant.map((d) => (
              <li key={voorvertoningPad(d.slug, d.type)}>
                <PubliekeKaart item={d} />
              </li>
            ))}
          </ul>
        </section>
      )}
      {(vorige || volgende) && (
        <nav className="publiek-vorige-volgende" aria-label="Vorige en volgende">
          {vorige && <Link to={voorvertoningPad(vorige.slug, vorige.type)}>← Vorige: {vorige.title}</Link>}
          {volgende && (
            <Link to={voorvertoningPad(volgende.slug, volgende.type)} className="publiek-volgende">
              Volgende: {volgende.title} →
            </Link>
          )}
        </nav>
      )}
    </>
  );
}

function VoorvertoningPage({ soort }) {
  const { slug } = useParams();
  const { api, app, naam } = SOORTEN[soort];
  const appPad = `${app}/${slug}`;
  const ingelogd = Boolean(localStorage.getItem('jwt'));
  const [doorloopje, setDoorloopje] = useState(null);
  const [loading, setLoading] = useState(true);
  // Alleen bij een echte 'bestaat niet' (ook: voorvertoningen uit) krijgt de pagina noindex.
  const [notFound, setNotFound] = useState(false);
  const { doorloopjes: lijst } = usePubliekeDoorloopjes();

  useEffect(() => {
    if (ingelogd) return;
    const fetchVoorvertoning = async () => {
      setLoading(true);
      setNotFound(false);
      setDoorloopje(null);
      try {
        const res = await axios.get(`${STRAPI_URL}${api}/${encodeURIComponent(slug)}`);
        setDoorloopje(res.data?.data || null);
      } catch (err) {
        if (err.response?.status === 404) setNotFound(true);
        else console.error('Fout bij het ophalen van de voorvertoning:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchVoorvertoning();
    window.scrollTo(0, 0);
  }, [slug, ingelogd, api]);

  if (ingelogd) return <Navigate to={appPad} replace />;

  return (
    <PubliekePagina naInloggen={appPad}>
      {({ openLogin, openRegister }) => {
        if (loading) return <p className="publiek-melding">Doorloopje wordt geladen...</p>;
        if (!doorloopje)
          return (
            <div className="publiek-melding">
              <SEO title="Pagina niet gevonden | Doorloopjes" description="Deze pagina bestaat niet of is verplaatst." noindex={notFound} />
              <h1>{notFound ? 'Pagina niet gevonden' : 'Deze pagina laden lukte niet'}</h1>
            </div>
          );

        const stap = doorloopje.first_step;
        const beschrijving = toDescription(doorloopje.description, `Bekijk "${doorloopje.title}" op Doorloopjes.`);
        const kop = soort === 'doorloopje' ? doorloopje.theme?.title || 'Algemeen' : naam;
        const omgeving = lijst && buren(lijst, soort, doorloopje.slug);
        return (
          <div className="publiek-voorvertoning">
            <SEO
              title={`${doorloopje.title} | Doorloopjes`}
              description={beschrijving}
              image={stap?.image?.url}
              url={voorvertoningPad(doorloopje.slug, soort)}
              type="article"
            />
            <Link to={OVERZICHT_PAD} className="publiek-terug">
              ← Alles voor je lespraktijk
            </Link>

            <header className="detail-page-header">
              <div className="header-title-block">
                <p className="detail-breadcrumb">{kop.toUpperCase()}</p>
                <h1 className="detail-title">{doorloopje.title}</h1>
              </div>
            </header>

            {doorloopje.book && (
              <div className="book-info-row">
                <div className="book-info">
                  Boek {doorloopje.book}, Pagina {doorloopje.page}
                </div>
              </div>
            )}

            {doorloopje.description?.length > 0 && (
              <div className="description-wrapper">
                <div className="detail-description">
                  <Beschrijving blokken={doorloopje.description} markdown={soort !== 'doorloopje' && !stap} />
                </div>
              </div>
            )}

            <div className="steps-grid">
              {stap && (
                <StapKaart nummer={1} titel={stap.title} afbeelding={stap.image?.url} alt={stap.image?.alt || 'Stap 1'}>
                  {stap.description?.length > 0 && <BlocksRenderer content={stap.description} />}
                </StapKaart>
              )}
              {doorloopje.other_steps.map((s, i) => (
                <StapKaart key={i} nummer={i + 2} titel={s.title} afbeelding={s.image?.url} alt={s.image?.alt || `Stap ${i + 2}`}>
                  <div className="publiek-stap-vervaagd" aria-hidden="true">
                    <span />
                    <span />
                    <span />
                    <span />
                  </div>
                  <p className="publiek-stap-slot">
                    <LockIcon className="publiek-slot-icoon" aria-hidden="true" />
                    <span>
                      <button type="button" className="publiek-link" onClick={openLogin}>
                        Log in
                      </button>{' '}
                      of{' '}
                      <button type="button" className="publiek-link" onClick={openRegister}>
                        maak gratis een account aan
                      </button>{' '}
                      om verder te lezen
                    </span>
                  </p>
                </StapKaart>
              ))}
            </div>

            {doorloopje.downloads?.length > 0 && (
              <>
                <h3 className="related-title">Download</h3>
                <div className="downloads-list">
                  {doorloopje.downloads.map((bestand) => (
                    <a key={bestand.url} href={bestand.url} target="_blank" rel="noopener noreferrer" className="download-link">
                      {bestand.name}
                    </a>
                  ))}
                </div>
              </>
            )}

            <p className="publiek-uitnodiging">
              Gratis voor leraren.{' '}
              <button type="button" className="publiek-link" onClick={openRegister}>
                Maak een account aan
              </button>{' '}
              of{' '}
              <button type="button" className="publiek-link" onClick={openLogin}>
                log in
              </button>{' '}
              om {stap ? 'alle stappen' : 'alle Doorloopjes'} te zien.
            </p>

            {omgeving && <Buren {...omgeving} />}
          </div>
        );
      }}
    </PubliekePagina>
  );
}

export default VoorvertoningPage;
